
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IHotelInfo } from '@common-types';
import { BookingErrorsEnum, ISessionKey, ITrustYouReview } from '@share/common-types';
import {
  SESSION_SEARCH_EXPIRED_MESSAGE,
  SESSION_EXPIRED_STATUS,
  Urls,
  Routes,
  SESSION_KEY_LABEL_ROOMS,
  SESSION_KEY_LABEL,
} from '@share/constants';
import { SERVER_ERROR_STATUSES } from '@share/constants';
import {
  attachCertificateContextToBody,
  buildCertificateContextFromSelection,
  getHeaders,
  axiosInstance,
  AppThunk,
  getHotelsRequest,
  UrlUtils,
  getSessionObj,
  delay,
  getSelectedCurrency,
  getTimeout,
  ICertificateRouteHint,
  RootState,
  isDisplayCertificates,
} from '@share/utils';
import { ItemTypeEnum } from '@share/common-types';
import { datesActions, hotelsActions, locationActions, roomsActions, trustYouReviewActions } from '@share/store/slices';
import { NULL_VALUE } from '@constants';

import { getRoomsDetails, handleRoomsDetailsError } from './rooms-search';
import { roomsSearchActions } from '../slices';
// Imported by path rather than from `@utils`, whose barrel reaches back into the store.
import { decodeHotelName, QUINTESS_SUPPLIER_ID } from '../../utils/hotel';

export interface IHotelDetailsState {
  hotel: IHotelInfo | null | undefined;
  loading: boolean;
  loadingRoom: boolean;
  isServerError: boolean;
  error: string;
}

const initialState: IHotelDetailsState = {
  hotel: null,
  loading: true,
  loadingRoom: true,
  error: '',
  isServerError: false
};

const zero = 0;

const hotelDetailsSlice = createSlice({
  name: 'hotel-details',
  initialState,
  reducers: {
    setLoading: (state: IHotelDetailsState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setLoadingRoom: (state: IHotelDetailsState, { payload }: PayloadAction<boolean>) => {
      state.loadingRoom = payload;
    },
    setError: (state: IHotelDetailsState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setHotelDetails: (state: IHotelDetailsState, { payload }: PayloadAction<IHotelInfo | null | undefined>) => {
      // Availability replaces the whole property rather than merging into it, and only
      // the static call reports the supplier. Carry it across so the layout does not
      // change under the user when the live details land. Guarded on the property id so
      // navigating to another property cannot inherit the previous one's supplier.
      const previous = state.hotel?.hotelDetails;
      const incoming = payload?.hotelDetails;

      if (!incoming) {
        state.hotel = payload;

        return;
      }

      const carriesSupplier =
        !!previous?.supplierId && !incoming.supplierId && incoming.id === previous.id;

      state.hotel = {
        ...payload,
        hotelDetails: {
          ...incoming,
          ...(carriesSupplier ? { supplierId: previous?.supplierId } : {}),
          // Three calls write this property and each sends the name HTML-escaped
          // ("Mickey&#39;s"), so it is decoded here rather than in any one of them.
          title: decodeHotelName(incoming.title),
        },
      } as IHotelInfo;
    },
    setIsServerError: (state: IHotelDetailsState, { payload }: PayloadAction<boolean>) => {
      state.isServerError = payload;
    },
  },
});

export const hotelDetailsActions = hotelDetailsSlice.actions;
export const { setLoading, setLoadingRoom, setError, setHotelDetails, setIsServerError } = hotelDetailsSlice.actions;

export const hotelDetailsReducer = hotelDetailsSlice.reducer;

const getHotel = async (
  hotelId: number,
  sessionKey: ISessionKey | undefined | null,
  dealId: number | undefined | null,
  quote: string | undefined | null,
  getState: () => RootState,
  certificateRoute?: ICertificateRouteHint,
) => {
  const { locationsStore, datesStore, roomsStore, loginStore, filtersStore, certificatesStore } = getState();
  const { selectedLocation } = locationsStore;
  const { startDate, endDate } = datesStore;
  const { rooms } = roomsStore;
  const { lifeStyle } = loginStore;

  const body: Record<string, unknown> = {
    //marginator: {percentage: 0},
    sessionKey,
    hotelRequest: {
      ...getHotelsRequest(rooms, startDate, endDate, selectedLocation?.code, true),
      hotelId,
    },
    currency: getSelectedCurrency(loginStore.account),
    dealId,
    quote,
    lifeStyle,
  };

  const displayCertificates = isDisplayCertificates(loginStore.account);
  if (displayCertificates) {
    const certificateContext = buildCertificateContextFromSelection({
      certificates: certificatesStore?.data,
      itemType: ItemTypeEnum.Hotel,
      storeCertificateId: filtersStore.certificateId,
      storeCertificateItemId: filtersStore.certificateItemId,
      routeCertificateId: certificateRoute?.certificateId,
      routeCertificateItemId: certificateRoute?.certificateItemId,
    });
    attachCertificateContextToBody(body, certificateContext);
  }

  return await axiosInstance.post(
    Urls.HotelDetails,
    body,
    { ...getHeaders() },
  );
}

export const updateSavedPropertyDetails = (propertyId: number, added: boolean): AppThunk => {
  return async (dispatch, getState) => {

    const { hotelDetailsStore } = getState();
    const { hotel } = hotelDetailsStore;
     
    if (hotel) {
      const cloned = JSON.parse(JSON.stringify(hotel));
      cloned.hotelDetails.savedProperty = cloned.hotelDetails.id == propertyId ? added : cloned.savedProperty;
      dispatch(setHotelDetails(cloned));
    }
  };
};

const getHotelRetry = async (
  hotelId: number,
  sessionKey: ISessionKey | undefined | null,
  dealId: number | undefined | null,
  quote: string | undefined | null,
  getState: () => RootState,
  certificateRoute?: ICertificateRouteHint,
) => {
  try {
    return await getHotel(hotelId, sessionKey, dealId, quote, getState, certificateRoute);
  } catch (error) {
    delay(300);
    return await getHotel(hotelId, sessionKey, dealId, quote, getState, certificateRoute);
  }
};

export const getHotelDetails = (
  hotelId: number,
  sessionKey: ISessionKey | undefined | null,
  dealId?: number | undefined | null,
  quote?: string | undefined | null,
  isStaticDetail?: boolean | undefined | null,
  isRoomLoading?: boolean,
  skipError?: boolean,
  certificateRoute?: ICertificateRouteHint,
): AppThunk => {
  return async (dispatch, getState) => {
    dispatch(handleHotelDetailsLoading(isStaticDetail, isRoomLoading, true));
    dispatch(setError(''));
    
    dispatch(setIsServerError(false));

    dispatch(datesActions.resetSelectedDates());
    dispatch(roomsActions.resetSelectedRooms());
    dispatch(locationActions.resetSelectedLocation());

    const { loginStore } = getState();
    const { account } = loginStore;

    try {
      const res: any = await getHotelRetry(
        hotelId,
        sessionKey as ISessionKey,
        dealId as number,
        quote as string,
        getState,
        certificateRoute,
      );

      dispatch(setHotelDetails(res.data));
      dispatch(trustYouReviewActions.setReviewModule(res.data?.trustYouData?.metaReview?.response as ITrustYouReview));

      if (res.data?.sessionKey?.isInvalid) {
        dispatch(getRoomsDetails(hotelId, sessionKey as ISessionKey, undefined, certificateRoute));
      } else {
        const values = UrlUtils.getValues();
        const session = getSessionObj(values[SESSION_KEY_LABEL_ROOMS] as ISessionKey, values[SESSION_KEY_LABEL] as ISessionKey);
        const timeoutSessionStoreExpired = getTimeout(session?.expireDate) <= 0;

        if ((!quote && isUndefined(session)) || (!!quote && timeoutSessionStoreExpired)) {
          dispatch(roomsSearchActions.setSessionKey(res.data.sessionKey));
        }

        if (isUndefined(session) || (!!quote && timeoutSessionStoreExpired)) {
          dispatch(hotelsActions.setSessionKey(res.data.sessionKey));
        }
      }
      
      dispatch(handleHotelDetailsLoading(isStaticDetail, isRoomLoading, false));

    } catch (error: any) {
      if (!skipError) {
        console.error(error);
        dispatch(handleHotelDetailsLoading(isStaticDetail, isRoomLoading, false));
        
        if (isStaticDetail) {
          dispatch(handleRoomsDetailsError(error));
        } else {
          dispatch(setError(!isEmpty(error?.code) ? error?.code?.toString() : 'GENERIC'));
          
          if (
            error?.response?.status === SESSION_EXPIRED_STATUS &&
            error?.response?.data &&
            error?.response?.data[zero] === SESSION_SEARCH_EXPIRED_MESSAGE
          ) {
            history.replaceState(null, NULL_VALUE, `/${account.name}${Routes.Search}${location.search}`);
            location.reload();
          } else if (SERVER_ERROR_STATUSES.includes(error?.response?.status)) {
            dispatch(setIsServerError(true));
          } else if ([BookingErrorsEnum.RoomsUnavailable].includes(error?.response?.data?.code)) {
            dispatch(setError(BookingErrorsEnum.RoomsUnavailable));
          }
        }
      }
    }
  };
};

export const handleHotelDetailsLoading = (isStaticDetail: boolean | undefined | null, isRoomLoading: boolean | undefined | null, loading: boolean): AppThunk => {
  return async (dispatch) => {
    if (isStaticDetail) {
      dispatch(roomsSearchActions.setIsRoomsLoading(loading));
    } else if (isRoomLoading) {
      dispatch(setLoadingRoom(loading));
    } else {
      dispatch(setLoading(loading));
    }
  }
}

export const getHotelStaticDetails = (hotelId: number, isStaticDetail: boolean | undefined | null, skipRoomContent: boolean = false, onSuccess?: () => void): AppThunk => {
  return async (dispatch, getState) => {
    dispatch(setLoading(true));

    dispatch(setError(''));
    
    dispatch(setIsServerError(false));

    const { loginStore } = getState();
    const { account } = loginStore;

    try {
      const res = await axiosInstance.get(`/property/static/${hotelId}`);

      if (res.data) {        
        // The static service names the property's supplier, so the layout is settled
        // here rather than waiting on availability. Read defensively: the field sits
        // beside the property, with older records carrying it under `details`.
        const rawSupplierId = res.data.supplierId ?? res.data.details?.supplierId;
        const supplierId = isUndefined(rawSupplierId) || rawSupplierId === null
          ? undefined
          : String(rawSupplierId);

        // Whole-house stays render their unit straight from this response while the
        // price is still loading, so the room content is kept even on the route that
        // otherwise drops it in favour of the one availability returns.
        const keepRoomContent = !skipRoomContent || supplierId === QUINTESS_SUPPLIER_ID;

        let roomsContent = keepRoomContent ? res.data.rooms?.map((room: any) => ({
          ...room,
          images: room.images?.map((image: any) => image.links?.map((link: any) => link.href)).flat() || [],
          descriptions: room.description ? [room.description] : [],
          amenities: room.amenities?.map((amenity: any) => amenity.name) || [],
        })) || [] : []
        
        const roomcontentWithImages = roomsContent.filter((room: any) => room.images && room.images.length > 0);
        const roomcontentSelection = roomcontentWithImages?.length > 6 ? roomcontentWithImages : roomsContent;

        roomsContent = roomcontentSelection.sort((a: any, b: any) => {
          // Sort rooms with images first
          const aHasImages = a.images && a.images.length > 0;
          const bHasImages = b.images && b.images.length > 0;
          
          if (aHasImages && !bHasImages) return -1;
          if (!aHasImages && bHasImages) return 1;
          return 0;
        });

        const additionalDescriptions: any[] = [];
        if (res.data.details?.descriptions?.attractions) {
          additionalDescriptions.push(res.data.details?.descriptions?.attractions);
        }
        if (res.data.details?.descriptions?.businessAmenities) {
          additionalDescriptions.push(res.data.details?.descriptions?.businessAmenities);
        }
        if (res.data.details?.descriptions?.dining) {
          additionalDescriptions.push(res.data.details?.descriptions?.dining);
        }

        const amenityDescriptions: any[] = [];
        if (res.data.details?.descriptions?.amenities) {
          amenityDescriptions.push(res.data.details?.descriptions?.amenities);
        }

        const checkInInformation: any[] = [];
        if (res.data.details?.checkin?.instructions) {
          checkInInformation.push(res.data.details?.checkin?.instructions);
        }
        if (res.data.details?.checkin?.beginTime) {
          checkInInformation.push(res.data.details?.checkin?.beginTime);
        }
        if (res.data.details?.checkin?.endTime) {
          checkInInformation.push(res.data.details?.checkin?.endTime);
        }
        if (res.data.details?.checkin?.specialInstructions) {
          checkInInformation.push(res.data.details?.checkin?.specialInstructions);
        }

        const checkOutInformation: any[] = [];
        if (res.data.details?.checkout?.time) {
          checkOutInformation.push(res.data.details?.checkout?.time);
        }
        if (res.data.details?.checkout?.specialInstructions) {
          checkOutInformation.push(res.data.details?.checkout?.specialInstructions);
        }

        const descriptions: any[] = [];
        if (res.data.details?.descriptions?.headline) {
          descriptions.push(res.data.details?.descriptions?.headline);
        }
        // Where a rental keeps its whole description, and the only place it appears:
        // without these the overview is blank until availability lands, if at all.
        if (res.data.details?.descriptions?.generalInformation) {
          descriptions.push(res.data.details?.descriptions?.generalInformation);
        }
        if (res.data.details?.descriptions?.propertyInformation) {
          descriptions.push(res.data.details?.descriptions?.propertyInformation);
        }
        if (res.data.details?.descriptions?.rooms) {
          descriptions.push(res.data.details?.descriptions?.rooms);
        }
        if (res.data.details?.descriptions?.dining) {
          descriptions.push(res.data.details?.descriptions?.dining);
        }

        const locationDescriptions: any[] = [];
        if (res.data.details?.descriptions?.location) {
          locationDescriptions.push(res.data.details?.descriptions?.location);
        }

        const otherImportantInformation: any[] = [];
        if (res.data.details?.fees?.optional) {
          otherImportantInformation.push(res.data.details?.fees?.optional);
        }
        if (res.data.details?.policies?.knowBeforeYouGo) {
          otherImportantInformation.push(res.data.details?.policies?.knowBeforeYouGo);
        }

        const details: any = {
          hotelDetails: {
            activities: res.data.details?.activities,
            additionalDescriptions,
            address: res.data.address,
            airports: res.data?.details?.airports,
            amenities: res.data.amenities?.length ? 
                            res.data.amenities.map((a: any, index: number) => ({
                              amenityId: index + 1,
                              categoryName: 'General',
                              name: a.amenityName,
                            })) : 
                            [],
            amenityDescriptions,
            brand: res.data?.details?.brand?.name,
            chain: res.data?.details?.chain?.name,
            chainCode: res.data?.details?.chain?.id,
            checkInInformation,
            checkOutInformation,
            city: res.data.cityName,
            countryCode: res.data.countryCode,
            descriptions,
            //distances: [], // VER esto
            email: res.data.email,
            //facilityDescriptions: [], // VER esto
            guestRatings: res.data.details?.guestRatings,
            id: res.data.propertyId,
            images: res.data.details?.images,
            importantInformation: [], // VER esto


            //languages: res.data.details?.languages,
            location: {
              latitude: res.data.lat,
              longitude: res.data.lng,
            },
            locationDescriptions,
            //onSitePayments: res.data.details?.onSitePayments,
            otherImportantInformation,
            pets: [], // VER esto
            phone: res.data.phone,
            recreationalActivities: res.data.details?.recreationalActivities,
            reviews: res.data.reviews,
            reviewsCount: res.data.reviewsCount,
            roomsCount: skipRoomContent ? 0 : res.data.rooms?.length,
            starRating: res.data.starRating,
            state: res.data.stateCode,
            //statistics: res.data.details?.statistics,
            thumb: res.data.thumb,
            title: res.data.name,
            //transportation: res.data.details?.transportation,
            zipCode: res.data.zipCode,

            category: 'Hotel',
            facilities: res.data.details?.facilities,
            specialInstructions: res.data.details?.specialInstructions,
            other: null,

            supplierId,
            // Quintess sells whole homes only, and the static payload carries no flag of
            // its own. Set here so the rental sections render from the first paint rather
            // than appearing when the live details land.
            isVacationRental: supplierId === QUINTESS_SUPPLIER_ID,
            dealPrice: 0,
            dealSavingPercentage: 0,
            savedProperty: false,
            withoutTaxes: false
          },

          packages: [],
          trustYouData: null,
          adjustedPackageGroups: [],
          roomsContent,
        };

        dispatch(setHotelDetails(details));

        onSuccess?.();
      }
      
      dispatch(setLoading(false));

      if (isStaticDetail) {
        dispatch(setLoadingRoom(false));
      }
  
    } catch (error: any) {
      console.error(error);
      dispatch(setError(!isEmpty(error?.code) ? error?.code?.toString() : 'GENERIC'));
      dispatch(setLoading(false));

      if (isStaticDetail) {
        dispatch(setLoadingRoom(false));
      }
  
      if (
        error?.response?.status === SESSION_EXPIRED_STATUS &&
        error?.response?.data &&
        error?.response?.data[zero] === SESSION_SEARCH_EXPIRED_MESSAGE
      ) {
        history.replaceState(null, NULL_VALUE, `/${account.name}${Routes.Search}${location.search}`);
        location.reload();
      } else if (SERVER_ERROR_STATUSES.includes(error?.response?.status)) {
        dispatch(setIsServerError(true));
      }
    }
  };
};


